import {createSlice} from '@reduxjs/toolkit';

import {iomoLuminaires as luminaires} from './luminaires/iomoLuminaires';
import {iomoLayouts as layouts} from './layouts/iomoLayouts';
import { selectIomoOffsetFromTop, selectIomoOffsetFromBottom} from './iomoSelectors';


const maxBranchesAmount = 8;
const customLayoutType = 'custom';
const noLayoutType = 'none';

const singleLumConfig = {
	active: false,
	type: 'luminaire', //luminaire
	height: 6,
	la: 0,
	angle: 0,
	roll: 0,
	pitch: 40,
	// dimmingLevel: null,
	housingSize: '195',
	bracket: '195',
	colourTemperature: '3000K',
	lightDistribution: '45',
	reflectorColour: 'gold',
	cap: 'short', // short, long, asymmetric
	modifier: 'none', // none, honeycomb

	accessories: 'None',
	spcDecisions: null,
	collisions: [],
};

const iomoInitialState = {
	status: 'idle',
	data: {
		design: 'pole',
		threeDReady: false,
		general: {
			poleHeight: 9,
			poleDiameter: '140',
			poleShape: 'cylindrical',
			numberOfHeads: maxBranchesAmount,
			maxNumberOfHeads: maxBranchesAmount,
			layout: 'spiral',
			heightMax: 9.20,
			heightMin: 5.00,
			angleMax: 315,
			angleMin: 0,
			pitch: 40,
			roll: 0,
			base: 'buriedbase',
			separateBuriedBase: 'none',
			secondDoor: 'none',
			fuseBox: 'none',
			buriedBaseSize: 1200,
			colorTheme: 'black',
			customColor: {
				use: false,
				base: {
					used: true,
					material: 'blackMatte'
				},
				head: {
					used: false,
					material: 'blackMatte',
				},
				ring: {
					used: false,
					material: 'blackGloss'
				}
			},
			colorThemeSwapped: false,
			bracket: '195',

			// general luminaire settings, might be overridden by individual head
			opticalSystem: 'Comfort', // comfort, performance
			housingSize: '195', //160, 195, 250
			lightDistribution: '30',
			colourTemperature: '3000K',
			reflectorColour: 'silver', // gold, silver
			cap: 'short', // short, long, asymmetric
			modifier: 'none', // none, honeycomb
			//dimmingLevel: null,

			// environment;
			environment: 'day',
			environmentIntensity: 1,

			// material settings
			useTestMaterial: false,
			testMaterial1: {
				color: '#999999',
				emissive: '#000000',

				roughness: 0.7, // 0-1
				metalness: 0.3, // 0-1

				clearcoat: 0, // 0-1
				clearcoatRoughness: 0, // 0-1
				ior: 1.5, // 1.0 - 2.333
				iridescence: 0, // 0-1
				iridescenceIOR: 1.3, // 1.0 - 2.333
				reflectivity: 0, // 0-1

				sheen: 0, // 0-1
				sheenRoughness: 1, // 0-1
				sheenColor: '#000000',

				normalScale: 0,
				clearcoatNormalScale: 0,

				specularIntensity: 1, // 0-1
				specularColor: '#ffffff',
			},
			testMaterial2: {
				color: '#505050',
				emissive: '#000000',

				roughness: 0.3, // 0-1
				metalness: 0.7, // 0-1

				clearcoat: 0, // 0-1
				clearcoatRoughness: 0, // 0-1
				ior: 1.5, // 1.0 - 2.333
				iridescence: 0, // 0-1
				iridescenceIOR: 1.3, // 1.0 - 2.333
				reflectivity: 0, // 0-1

				sheen: 0, // 0-1
				sheenRoughness: 1, // 0-1
				sheenColor: '#000000',

				specularIntensity: 1, // 0-1
				specularColor: '#ffffff',
			}
		},
		heads: [], // set afterwards
		selectedHead: null,
		projectName: '',
		headTableVisible: false,
	},
	error: {},
};

const general = iomoInitialState.data.general;
const firstLayout = layouts[general.layout].calculate(iomoInitialState.data.design, iomoInitialState.data.general);

iomoInitialState.data.heads =  Array.from({length: maxBranchesAmount}, (v, index) => {
	const head = {
		...singleLumConfig
	};
	head.active = index < firstLayout.branches.length;
	const branch = head.active ? firstLayout.branches[index] : {};
	for (const key of Object.keys(head)) {
		if (key in general) {
			head[key] = general[key];
		} else if (key in branch) {
			head[key] = branch[key];
		}
	}
	return head;
});

const userIntendedFeatures = {
	poleHeight: general.poleHeight,
	numberOfHeads: general.numberOfHeads,
	heightMax: general.heightMax,
	heightMin: general.heightMin,
};

const forcedFeatures = {};

function tryRestoringUserIntendedFeatures(oldConfig, newConfig, key, dependencies, maxValue) {
	let restored = false;
	let dependeciesChanged = false;
	for (const depKey of dependencies) {
		if ((depKey === 'heightMin' && newConfig[depKey] < oldConfig[depKey]) || (depKey !== 'heightMin' && newConfig[depKey] > oldConfig[depKey])) {
			dependeciesChanged = true;
			break;
		}
	}
	if (dependeciesChanged) {
		const intendedValue = (key in userIntendedFeatures ? userIntendedFeatures[key] : null);
		if (intendedValue !== null && intendedValue > newConfig[key]) {
			const newValue = Math.min(intendedValue, maxValue);
			// console.log('RESTORING', key, newValue);
			if (newValue < intendedValue) {
				forcedFeatures[key] = newValue;
			}
			restored = true;
			newConfig[key] = newValue;
		}
	}
	return restored;
}


function applyLayoutToHeads(heads, layout) {
	for (let i = 0; i < heads.length; i++) {
		const head = heads[i];
		head.active = i < layout.branches.length;
		head.height = head.active ? layout.branches[i].height : singleLumConfig.height;
		head.angle = head.active ? layout.branches[i].angle : singleLumConfig.angle;
		head.pitch = head.active ? layout.branches[i].pitch : singleLumConfig.pitch;
		head.roll = head.active ? layout.branches[i].roll : singleLumConfig.roll;
	}
}


function recalculateLayout(data, oldConfig, newConfig, heads) {
	if (newConfig.layout === noLayoutType) {
		return;
	}
	const DesignClass = luminaires[data.design];
	if (newConfig.layout !== customLayoutType) {
		let layout = layouts[newConfig.layout].calculate(data.design, newConfig);
		data.general.maxNumberOfHeads = layout.maxNumberOfHeads;
		if (layout.maxNumberOfHeads < newConfig.numberOfHeads) {
			forcedFeatures.numberOfHeads = layout.branches.length;
			newConfig.numberOfHeads = layout.branches.length;
		} else if (layout.maxNumberOfHeads > newConfig.numberOfHeads) {
			const restored = tryRestoringUserIntendedFeatures(oldConfig, newConfig, 'numberOfHeads', ['poleHeight', 'heightMin', 'heightMax'], layout.maxNumberOfHeads);
			if (restored) {
				layout = layouts[newConfig.layout].calculate(data.design, newConfig);
				newConfig.numberOfHeads = layout.branches.length;
			}
		}
		applyLayoutToHeads(heads, layout);
	} else {
		for (let i = 0; i < heads.length; i++) {
			const head = heads[i];
			head.active = i < newConfig.numberOfHeads;
			head.height = head.active ? Math.min(DesignClass.poleSizes[DesignClass.getPoleId(newConfig)].maxHeight, head.height) : singleLumConfig.height;
			head.angle = head.active ? head.angle : singleLumConfig.angle;
		}
	}
	if (DesignClass.internalOffsets)	 {
		DesignClass.calculateOffsets(newConfig,	heads, true);
	}

	if (data.selectedHead !== null && data.selectedHead >= newConfig.numberOfHeads) {
		data.selectedHead = 0;
	}
}


function updateHeadHeight(state, index, height) {
	state.data.heads[index].height = height;
	state.data.general.layout = customLayoutType;

	const DesignClass = luminaires[state.data.design];
	if (DesignClass.internalOffsets) {
		const offsets = DesignClass.calculateOffsetsByHeight(
			state.data.general,
			height,
			state.data.heads[index].housingSize ? state.data.heads[index].housingSize : state.data.general.housingSize,
			state.data.heads[index].opticalSystem ? state.data.heads[index].opticalSystem : state.data.general.opticalSystem,
			state.data.heads,
			index
		);
		state.data.heads[index].la = offsets.la;
	}
}


function getClosestValue(invalidValue, values) {
	let diff = null;
	let selectedValue = null;
	for (const value of values) {
		if (value !== invalidValue) {
			const newDiff = Math.abs(value - invalidValue);
			if (selectedValue === null || newDiff <= diff) {
				selectedValue = value;
				diff = newDiff;
			}
		}
	}
	return selectedValue;
}

function enforceConstraints(state) {
	const data = state.data;
	const changed = {};
	const designName = data.design;
	const DesignClass = luminaires[designName];
	if ('poleConstraints' in DesignClass) {
		const constraints = DesignClass.poleConstraints;
		let poleHeight = +data.general.poleHeight;
		let poleDiameter = +data.general.poleDiameter;
		const poleShape = +data.general.poleShape;

		if (poleShape in constraints) {
			const entry = constraints[poleShape];
			if ('poleHeight' in entry && !entry.poleHeight.includes(poleHeight)) {
				poleHeight = 'poleHeightFallback' in entry ? entry.poleHeightFallback : getClosestValue(poleHeight, entry.poleHeight);
			}
			if ('poleDiameter' in entry) {
				const diameterEntry = entry.poleDiameter[poleHeight];
				if (!diameterEntry.includes(poleDiameter)) {
					poleDiameter = 'poleDiameterFallback' in entry ? entry.poleDiameterFallback : getClosestValue(poleDiameter, diameterEntry);
				}
			}
		}
		if (poleHeight !== data.general.poleHeight) {
			changed.poleHeight = data.general.poleHeight;
			data.general.poleHeight = poleHeight;

			const candidateHeightMax = poleHeight - selectIomoOffsetFromTop(state);
			const candidateHeightMin = Math.min(selectIomoOffsetFromBottom(state), candidateHeightMax);
			if (data.general.heightMax > candidateHeightMax || data.general.heightMin < candidateHeightMin) {
				changed.heightMax = data.general.heightMax;
				changed.heightMin = data.general.heightMin;
				data.general.heightMax = candidateHeightMax;
				data.general.heightMin = candidateHeightMin;
			}
		}
		if (poleDiameter !== data.general.poleDiameter) {
			changed.poleDiameter = data.general.poleDiameter;
			data.general.poleDiameter = poleDiameter;
		}
	}
	return changed;
}


function setHeadsData(payload, state) {
	console.log('setIomoHeadsData', payload);
	const oldGeneralConfig = {...state.data.general};
	const generalConfig = state.data.general;
	let sameHousingSize = 0;
	let housingSize = null;
	payload.heads.forEach((head, index) => {
		if ('housingSize' in head) {
			if (housingSize === null || head.housingSize === housingSize) {
				sameHousingSize++;
			}
			housingSize = head.housingSize;
		}
		Object.assign(state.data.heads[index], head);
	});
	// if we set all the single heads' housing size the same value, we set also the global one
	if (sameHousingSize === maxBranchesAmount) {
		generalConfig.housingSize = housingSize;
	}
	const DesignClass = luminaires[state.data.design];

	if (DesignClass.supportLayouts) {
		recalculateLayout(state.data, oldGeneralConfig, generalConfig, state.data.heads);
	} else if (!DesignClass.singleBracket && DesignClass.internalOffsets) {
		DesignClass.calculateOffsets(state.data.general, state.data.heads, true);
	}
}

export const iomoSlice = createSlice({
	name: 'iomo',
	initialState: iomoInitialState,
	reducers: {
		setIomoDesign: (state, action) => {
			const currentDesign = state.data.design;
			const newDesign = action.payload.id;
			const isChanged = currentDesign !== newDesign;
			if (isChanged) {
				state.data.design = newDesign;
				Object.assign(state.data.general, luminaires[newDesign].defaultConfig);
				const DesignClass = luminaires[newDesign];
				let offsets = [];
				if (!DesignClass.singleBracket && DesignClass.internalOffsets) {
					offsets = DesignClass.calculateOffsets(state.data.general, state.data.heads);
				}
				for (let i = 0; i < state.data.heads.length; i++) {
					state.data.heads[i].active = i < state.data.general.numberOfHeads;
					state.data.heads[i].collisions = [];
					if (offsets.length > i) {
						state.data.heads[i].la = offsets[i].la;
					}
				}
				if (luminaires[newDesign].supportLayouts) {
					recalculateLayout(state.data, state.data.general, state.data.general, state.data.heads);
				}
			}
		},


		setPoleHeight: (state, action) => {
			const oldGeneralConfig = {...state.data.general};
			const generalConfig = state.data.general; // shortcut

			const poleHeight = +action.payload.id;
			state.data.general.poleHeight = poleHeight;
			const changed = enforceConstraints(state);
			userIntendedFeatures.poleHeight = poleHeight;
			delete forcedFeatures.poleHeight;
			if ('heightMax' in changed) {
				forcedFeatures.heightMax = state.data.general.heightMax;
				forcedFeatures.heightMin = state.data.general.heightMin;
			}

			const distance = poleHeight - state.data.general.heightMin;

			// when changing pole, force heightmin to have some space for heads
			if (distance <= 2) {
				state.data.general.heightMin = Math.max(3, poleHeight - 2);
			}

			if (luminaires[state.data.design].supportLayouts) {
				tryRestoringUserIntendedFeatures(oldGeneralConfig, generalConfig, 'heightMax', ['poleHeight'], generalConfig.poleHeight);
			}
			if (luminaires[state.data.design].supportLayouts) {
				recalculateLayout(state.data, oldGeneralConfig, generalConfig, state.data.heads);
			}
		},
		setPoleDiameter: (state, action) => {
			state.data.general.poleDiameter = action.payload.id;
		},
		setPoleShape: (state, action) => {
			state.data.general.poleShape = action.payload.id;
		},
		setPoleData: (state, action) => {
			state.data.general.poleShape = action.payload.poleShape;
			state.data.general.poleHeight = action.payload.poleHeight;
			state.data.general.poleDiameter = action.payload.poleDiameter;
		},

		setNumberOfHeads: (state, action) => {
			const oldGeneralConfig = {...state.data.general};
			const generalConfig = state.data.general;
			generalConfig.numberOfHeads = action.payload.id;
			userIntendedFeatures.numberOfHeads = generalConfig.numberOfHeads;
			delete forcedFeatures.numberOfHeads;
			if (luminaires[state.data.design].supportLayouts) {
				recalculateLayout(state.data, oldGeneralConfig, generalConfig, state.data.heads);
			}
		},

		setHousingSize: (state, action) => {
			state.data.general.housingSize = action.payload.id;
		},
		setBracket: (state, action) => {
			state.data.general.bracket = action.payload.id;
			//
		},

		setIomoOpticalSystem: (state, action) => {
			state.data.general.opticalSystem = action.payload.id;
		},
		setIomoHousingSize: (state, action) => {
			state.data.general.housingSize = action.payload.id;
		},
		setIomoReflectorColour: (state, action) => {
			state.data.general.reflectorColour = action.payload.id;
		},
		setIomoLightDistribution: (state, action) => {
			state.data.general.lightDistribution = action.payload.id;
		},
		setIomoColourTemperature: (state, action) => {
			state.data.general.colourTemperature = action.payload.id;
		},
		setIomoModifier: (state, action) => {
			state.data.general.modifier = action.payload.id;
		},
		setIomoCap: (state, action) => {
			state.data.general.cap = action.payload.id;
		},
		setIomoColorTheme: (state, action) => {
			state.data.general.colorTheme = action.payload.id;
		},
		setIomoColorThemeSwapped: (state, action) => {
			state.data.general.colorThemeSwapped = action.payload.id;
		},
		setIomoCustomColorMaterial: (state, action) => {
			const newCustomColor = {...state.data.general.customColor};
			newCustomColor[action.payload.type].material = action.payload.material;
			state.data.general.customColor = newCustomColor;
		},
		setIomoCustomColorUsed: (state, action) => {
			const newCustomColor = {...state.data.general.customColor};
			newCustomColor[action.payload.type].used = action.payload.used;
			state.data.general.customColor = newCustomColor;
		},
		setIomoCustomColorUse: (state, action) => {
			const newCustomColor = {...state.data.general.customColor};
			newCustomColor.use = action.payload.use;
			state.data.general.customColor = newCustomColor;
		},
		setIomoEnvironment: (state, action) => {
			state.data.general.environment = action.payload.id;
		},
		setBase: (state, action) => {
			state.data.general.base = action.payload.id;
		},
		setSeparateBuriedBase: (state, action) => {
			state.data.general.separateBuriedBase = action.payload.id;
		},
		setSecondDoor: (state, action) => {
			state.data.general.secondDoor = action.payload.id;
		},
		setFuseBox: (state, action) => {
			state.data.general.fuseBox = action.payload.id;
		},
		setBuriedBaseSize: (state, action) => {
			state.data.general.buriedBaseSize = action.payload.id;
		},
		setHousingColourInlay: (state, action) => {
			state.data.general.housingColourInlay = action.payload.id;
		},
		setHousingColourPole: (state, action) => {
			state.data.general.housingColourPole = action.payload.id;
		},
		setLayout: (state, action) => {
			const oldGeneralConfig = {...state.data.general};
			const generalConfig = state.data.general;
			state.data.general.layout = action.payload.id;

			if (luminaires[state.data.design].supportLayouts) {
				recalculateLayout(state.data, oldGeneralConfig, generalConfig, state.data.heads);
			}
		},
		setMaterial: (state, action) => {
			state.data.general.material = action.payload.id;
		},
		setIomoData: (state, action) => {
			// console.log('setIomoData', action.payload);
			state.data = action.payload;
		},
		setMaxHeight: (state, action) => {
			const oldGeneralConfig = {...state.data.general};
			const generalConfig = state.data.general;
			generalConfig.heightMax = action.payload.value;
			// ensure distance between min and max
			generalConfig.heightMin = Math.min(generalConfig.heightMin, generalConfig.heightMax - 1);
			userIntendedFeatures.heightMax = generalConfig.heightMax;
			userIntendedFeatures.heightMin = generalConfig.heightMin;
			delete forcedFeatures.heightMax;
			delete forcedFeatures.heightMin;

			if (luminaires[state.data.design].supportLayouts) {
				recalculateLayout(state.data, oldGeneralConfig, generalConfig, state.data.heads);
			}

		},
		setMinHeight: (state, action) => {
			const oldGeneralConfig = {...state.data.general};
			const generalConfig = state.data.general;
			generalConfig.heightMin = action.payload.value;
			// ensure distance between min and max
			generalConfig.heightMax = Math.max(generalConfig.heightMax, generalConfig.heightMin + 1);
			userIntendedFeatures.heightMin = generalConfig.heightMin;
			userIntendedFeatures.heightMax = generalConfig.heightMax;
			delete forcedFeatures.heightMin;
			delete forcedFeatures.heightMax;

			if (luminaires[state.data.design].supportLayouts) {
				recalculateLayout(state.data, oldGeneralConfig, generalConfig, state.data.heads);
			}
		},
		setMaxAngle: (state, action) => {
			const oldGeneralConfig = {...state.data.general};
			const generalConfig = state.data.general;
			state.data.general.angleMax = action.payload.value;
			if (state.data.general.angleMax < state.data.general.angleMin) {
				state.data.general.angleMin = state.data.general.angleMax;
			}
			if (luminaires[state.data.design].supportLayouts) {
				recalculateLayout(state.data, oldGeneralConfig, generalConfig, state.data.heads);
			}
		},
		setMinAngle: (state, action) => {
			const oldGeneralConfig = {...state.data.general};
			const generalConfig = state.data.general;
			state.data.general.angleMin = action.payload.value;
			if (state.data.general.angleMax < state.data.general.angleMin) {
				state.data.general.angleMax = state.data.general.angleMin;
			}
			if (luminaires[state.data.design].supportLayouts) {
				recalculateLayout(state.data, oldGeneralConfig, generalConfig, state.data.heads);
			}
		},
		setAllPitch: (state, action) => {
			const oldGeneralConfig = {...state.data.general};
			const generalConfig = state.data.general;
			state.data.general.pitch = action.payload.value;
			if (luminaires[state.data.design].supportLayouts) {
				recalculateLayout(state.data, oldGeneralConfig, generalConfig, state.data.heads);
			}
		},
		setAllRoll: (state, action) => {
			const oldGeneralConfig = {...state.data.general};
			const generalConfig = state.data.general;
			state.data.general.roll = action.payload.value;
			if (luminaires[state.data.design].supportLayouts) {
				recalculateLayout(state.data, oldGeneralConfig, generalConfig, state.data.heads);
			}
		},
		setSelectedHead: (state, action) => {
			state.data.selectedHead = action.payload.selectedHead === null ? null : Math.max(0, Math.min(+action.payload.selectedHead, state.data.general.numberOfHeads - 1));
		},

		setSelectedHeadHeight: (state, action) => {
			const selectedHead = state.data.selectedHead;
			if (selectedHead !== null) {
				updateHeadHeight(state, selectedHead, action.payload.value);
			}
		},

		setIomoHeadHeight: (state, action) => {
			updateHeadHeight(state, action.payload.instance, action.payload.value);
		},

		// update data of a single head
		setIomoHeadData: (state, action) => {
			Object.assign(state.data.heads[action.payload.instance], action.payload.value);

			const DesignClass = luminaires[state.data.design];
			if (!DesignClass.singleBracket && DesignClass.internalOffsets) {
				const offsets = DesignClass.calculateOffsetsByHeight(
					state.data.general,
					state.data.heads[action.payload.instance].height,
					state.data.heads[action.payload.instance].housingSize ? state.data.heads[action.payload.instance].housingSize : state.data.general.housingSize,
					state.data.heads[action.payload.instance].opticalSystem ? state.data.heads[action.payload.instance].opticalSystem : state.data.general.opticalSystem,
					state.data.heads,
					action.payload.instance
				);
				state.data.heads[action.payload.instance].la = offsets.la;
			}
		},

		// update data of all heads
		setIomoHeadsData: (state, action) => {
			setHeadsData(action.payload, state);
		},
		setIomoAllHeadsData: (state, action) => {
			console.log('setIomoAllHeadsData', action.payload);
			const heads = Array.from({length: 8}, (_, i) => {
				const p = {};
				p[action.payload.key] = action.payload.value;
				return p;
			});
			setHeadsData({heads: heads}, state);
			state.data.general[action.payload.key] = action.payload.value;
		},

		setSelectedHeadAngle: (state, action) => {
			const selectedHead = state.data.selectedHead;
			if (selectedHead !== null) {
				state.data.heads[selectedHead].angle = action.payload.value;
				state.data.general.layout = customLayoutType;
			}
		},
		setIomoHeadAngle: (state, action) => {
			state.data.heads[action.payload.instance].angle = action.payload.value;
			state.data.general.layout = customLayoutType;
		},
		setIomoHeadPitch: (state, action) => {
			state.data.heads[action.payload.instance].pitch = action.payload.value;
			state.data.general.layout = customLayoutType;
		},
		setIomoHeadRoll: (state, action) => {
			state.data.heads[action.payload.instance].roll = action.payload.value;
			state.data.general.layout = customLayoutType;
		},
		setIomoHeadHousingSize: (state, action) => {
			state.data.heads[action.payload.instance].housingSize = action.payload.value;
		},
		setIomoHeadColourTemperature: (state, action) => {
			state.data.heads[action.payload.instance].colourTemperature = action.payload.value;
		},
		setIomoHeadLightDistribution: (state, action) => {
			state.data.heads[action.payload.instance].lightDistribution = action.payload.value;
		},
		setIomoHeadModifier: (state, action) => {
			state.data.heads[action.payload.instance].modifier = action.payload.value;
		},
		setIomoHeadCap: (state, action) => {
			state.data.heads[action.payload.instance].cap = action.payload.value;
		},
		setIomoHeadType: (state, action) => {
			state.data.heads[action.payload.instance].type = action.payload.value;
		},
		setIomoAccessories: (state, action) => {
			state.data.heads[action.payload.instance].accessories = action.payload.value;
		},
		setHeadTableVisible: (state, action) => {
			state.data.headTableVisible = action.payload.headTableVisible;
		},

		setTestMaterial1: (state, action) => {
			console.log('setTestMaterial1', action.payload.material);
			state.data.general.testMaterial1 = action.payload.material;
		},
		setTestMaterial2: (state, action) => {
			console.log('setTestMaterial2', action.payload.material);
			state.data.general.testMaterial2 = action.payload.material;
		},
		setUseTestMaterial: (state, action) => {
			state.data.general.useTestMaterial = action.payload.useTestMaterial;
		},
		setIomo3DReady: (state, action) => {
			state.data.threeDReady = action.payload.value;
		},

	},
	extraReducers: (builder) => {
	}
});

export const {
	setIomoDesign,
	setIomoOpticalSystem,
	setIomoColorTheme,
	setIomoColorThemeSwapped,
	setIomoHousingSize,
	setIomoReflectorColour,
	setIomoColourTemperature,
	setIomoLightDistribution,

	setPoleHeight,
	setPoleShape,
	setNumberOfHeads,
	setBracket,
	setHousingColourInlay,
	setHousingColourPole,
	setLayout,
	setMaterial,
	setIomoData,
	setMaxHeight,
	setMinHeight,
	setMaxAngle,
	setMinAngle,
	setAllPitch,
	setAllRoll,
	setSelectedHead,
	setSelectedHeadHeight,
	setSelectedHeadAngle,
	setIomoHeadHeight,
	setIomoHeadAngle,
	setIomoHeadPitch,
	setIomoHeadRoll,
	setIomoHeadData,
	setIomoHeadsData,
	setIomoAllHeadsData,
	setIomoHeadHousingSize,
	setIomoHeadColourTemperature,
	setIomoHeadLightDistribution,
	setIomoHeadModifier,
	setIomoHeadCap,
	setIomoHeadType,
	setHousingSize,
	setIomoModifier,
	setIomoCap,
	setPoleMaterial,
	setPoleDiameter,
	setBase,
	setSeparateBuriedBase,
	setBuriedBaseSize,
	setSecondDoor,
	setHeadTableVisible,
	setTestMaterial1,
	setTestMaterial2,
	setUseTestMaterial,
	setIomoEnvironment,
	setIomoCustomColorMaterial,
	setIomoCustomColorUsed,
	setIomoCustomColorUse,
	setPoleData,
	setIomo3DReady,
} = iomoSlice.actions;


export default iomoSlice.reducer;
